<template>
  <div class="schedules-page">
    <div class="container">
      <h1 class="title">Site Schedules</h1>
      <h2 class="subtitle">{{ site.name }}</h2>

      <div class="box">
        <div class="level">
          <div class="level-left">
            <div class="level-item">
              <router-link :to="`/sites/${siteId}`" class="button is-info">
                <span class="icon"><i class="mdi mdi-arrow-left"></i></span>
                <span>Back to Site</span>
              </router-link>
            </div>
          </div>
          <div class="level-right">
            <div class="level-item">
              <router-link :to="`/sites/${siteId}/schedules/create`" class="button is-primary">
                <span class="icon"><i class="mdi mdi-plus"></i></span>
                <span>Create New Schedule</span>
              </router-link>
            </div>
          </div>
        </div>

        <div v-if="loading" class="has-text-centered my-5">
          <span class="icon is-large"><i class="mdi mdi-loading mdi-spin"></i></span>
          <p>Loading schedules...</p>
        </div>

        <div v-else-if="schedules.length === 0" class="notification is-info has-text-centered">
          <p>No schedules found for this site.</p>
          <p class="mt-3">
            <router-link :to="`/sites/${siteId}/schedules/create`" class="button is-primary">
              <span class="icon"><i class="mdi mdi-plus"></i></span>
              <span>Create First Schedule</span>
            </router-link>
          </p>
        </div>

        <div v-else>
          <table class="table is-fullwidth is-striped is-hoverable">
            <thead>
              <tr>
                <th>Status</th>
                <th>Created</th>
                <th>Working Days</th>
                <th>Working Months</th>
                <th>Holidays</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="schedule in schedules" :key="schedule.id">
                <td>
                  <span 
                    class="tag" 
                    :class="schedule.is_active ? 'is-success' : 'is-light'"
                  >
                    {{ schedule.is_active ? 'Active' : 'Inactive' }}
                  </span>
                </td>
                <td>{{ formatDate(schedule.created_at) }}</td>
                <td>
                  <span class="tag is-info mr-1 mb-1" v-for="day in getWorkingDays(schedule)" :key="day.id">
                    {{ day.day_name }}
                  </span>
                </td>
                <td>
                  <span class="tag is-info mr-1 mb-1" v-for="month in getWorkingMonths(schedule)" :key="month.id">
                    {{ month.month_name }}
                  </span>
                </td>
                <td>{{ schedule.holidays.length }}</td>
                <td>
                  <div class="buttons are-small">
                    <router-link :to="`/schedules/${schedule.id}`" class="button is-info">
                      <span class="icon"><i class="mdi mdi-eye"></i></span>
                    </router-link>
                    <router-link :to="`/schedules/${schedule.id}/edit`" class="button is-warning">
                      <span class="icon"><i class="mdi mdi-pencil"></i></span>
                    </router-link>
                    <button 
                      v-if="!schedule.is_active" 
                      @click="activateSchedule(schedule.id)" 
                      class="button is-success"
                    >
                      <span class="icon"><i class="mdi mdi-check"></i></span>
                    </button>
                    <button 
                      @click="confirmDelete(schedule)" 
                      class="button is-danger"
                    >
                      <span class="icon"><i class="mdi mdi-delete"></i></span>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div class="modal" :class="{ 'is-active': showDeleteModal }">
      <div class="modal-background" @click="showDeleteModal = false"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">Confirm Deletion</p>
          <button class="delete" aria-label="close" @click="showDeleteModal = false"></button>
        </header>
        <section class="modal-card-body">
          Are you sure you want to delete this schedule? This action cannot be undone.
        </section>
        <footer class="modal-card-foot">
          <button class="button is-danger" @click="deleteSchedule">Delete</button>
          <button class="button" @click="showDeleteModal = false">Cancel</button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapActions } from 'vuex';

export default {
  name: 'SiteScheduleIndex',

  data() {
    return {
      siteId: null,
      site: {},
      schedules: [],
      loading: true,
      showDeleteModal: false,
      scheduleToDelete: null
    };
  },

  created() {
    this.siteId = this.$route.params.siteId;
    this.fetchData();
  },

  methods: {
    ...mapActions({
      showNotification: 'notifications/showNotification'
    }),


    async fetchData() {
      try {
        this.loading = true;

        // Fetch site details
        const siteResponse = await axios.get(`/api/sites/${this.siteId}`);
        this.site = siteResponse.data;

        // Fetch schedules
        const schedulesResponse = await axios.get(`/api/sites/${this.siteId}/schedules`);
        this.schedules = schedulesResponse.data.data;
      } catch (error) {
        this.showNotification({
          type: 'danger',
          message: 'Failed to load schedules: ' + (error.response?.data?.message || error.message)
        });
      } finally {
        this.loading = false;
      }
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString();
    },

    getWorkingDays(schedule) {
      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      return schedule.working_days.filter(day => day.is_working).map(day => {
        return {
          ...day,
          day_name: dayNames[day.day_of_week]
        };
      });
    },

    getWorkingMonths(schedule) {
      const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
      return schedule.working_months.filter(month => month.is_working).map(month => {
        return {
          ...month,
          month_name: monthNames[month.month - 1]
        };
      });
    },

    async activateSchedule(scheduleId) {
      try {
        await axios.patch(`/api/schedules/${scheduleId}/toggle-active`);
        this.showNotification({
          type: 'success',
          message: 'Schedule activated successfully'
        });
        this.fetchData(); // Refresh data
      } catch (error) {
        this.showNotification({
          type: 'danger',
          message: 'Failed to activate schedule: ' + (error.response?.data?.message || error.message)
        });
      }
    },

    confirmDelete(schedule) {
      this.scheduleToDelete = schedule;
      this.showDeleteModal = true;
    },

    async deleteSchedule() {
      if (!this.scheduleToDelete) return;

      try {
        await axios.delete(`/api/schedules/${this.scheduleToDelete.id}`);
        this.showNotification({
          type: 'success',
          message: 'Schedule deleted successfully'
        });
        this.fetchData(); // Refresh data
      } catch (error) {
        this.showNotification({
          type: 'danger',
          message: 'Failed to delete schedule: ' + (error.response?.data?.message || error.message)
        });
      } finally {
        this.showDeleteModal = false;
        this.scheduleToDelete = null;
      }
    }
  }
};
</script>
